import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Scale,
  Shield,
  FileText,
  ArrowRight,
  ExternalLink,
  HelpCircle,
} from "lucide-react";

const OripaRegulationGuidePage: React.FC = () => {
  // 賭博罪に該当しないための条件
  const legalConditions = [
    {
      condition: "商品の提供が確実",
      description: "抽選結果に関わらず、必ず何らかの商品（カード等）を受け取れる仕組みになっている",
      required: true,
    },
    {
      condition: "商品価値の透明性",
      description: "各賞品の内容や封入率が明示されており、ユーザーが判断できる状態にある",
      required: true,
    },
    {
      condition: "古物商許可証の取得",
      description: "中古カードを扱う場合は古物商許可証が必要",
      required: true,
    },
    {
      condition: "適正価格での販売",
      description: "商品価値に対して著しく高額な価格設定をしていない",
      required: true,
    },
  ];

  // 問題となりうるケース
  const problematicCases = [
    {
      case: "ハズレ扱いで商品なし",
      description: "抽選に外れた場合に何も受け取れない仕組み",
      risk: "賭博罪に該当する可能性が高い",
      level: "high",
    },
    {
      case: "ポイントバック型",
      description: "ハズレの場合にポイントのみ返却し、商品提供なし",
      risk: "賭博罪に該当する可能性がある",
      level: "high",
    },
    {
      case: "過度に低い封入率",
      description: "当たりの封入率が極端に低く、実質的に賭け的要素が強い",
      risk: "景品表示法違反の可能性",
      level: "medium",
    },
    {
      case: "虚偽の封入率表示",
      description: "表示している封入率と実際の封入率が異なる",
      risk: "詐欺罪・景品表示法違反",
      level: "high",
    },
    {
      case: "未成年への無制限販売",
      description: "年齢確認なく高額オリパを未成年に販売",
      risk: "社会的批判・規制強化の要因に",
      level: "medium",
    },
  ];

  // 関連法規
  const relatedLaws = [
    {
      name: "刑法（賭博罪）",
      article: "第185条・第186条",
      summary: "偶然の勝敗により財物を得失する行為は賭博罪に該当",
      relevance: "オリパが「賭博」と判断されると刑事罰の対象に",
    },
    {
      name: "古物営業法",
      article: "第3条",
      summary: "中古品の売買を業として行う場合は許可が必要",
      relevance: "中古カードを扱うオリパは古物商許可証が必須",
    },
    {
      name: "景品表示法",
      article: "第5条",
      summary: "商品・サービスの品質等について虚偽・誇大表示を禁止",
      relevance: "封入率や内容物の虚偽表示は違反となる",
    },
    {
      name: "特定商取引法",
      article: "第11条〜",
      summary: "通信販売における表示義務・クーリングオフ等を規定",
      relevance: "オンライン販売では表示義務の遵守が必要",
    },
    {
      name: "消費者契約法",
      article: "第4条〜",
      summary: "消費者を保護するための契約取消権等を規定",
      relevance: "誤認を招く説明は契約取消の対象に",
    },
  ];

  // ユーザー向け注意点
  const userTips = [
    {
      title: "運営会社の確認",
      description: "特定商取引法に基づく表記で、会社名・住所・連絡先が明示されているか確認",
    },
    {
      title: "封入率の確認",
      description: "各賞品の封入率が明示されているか。不明瞭な場合は要注意",
    },
    {
      title: "ハズレの扱い",
      description: "「ハズレ」がある場合、どのような商品が届くのか確認",
    },
    {
      title: "口コミ・評判",
      description: "SNSや口コミサイトで実際の利用者の声を確認",
    },
    {
      title: "返品・キャンセル規定",
      description: "購入後のキャンセルや返品に関する規定を事前に確認",
    },
  ];

  // 事業者向けチェックリスト
  const operatorChecklist = [
    { item: "古物商許可証を取得しているか", category: "必須" },
    { item: "すべての抽選結果で商品が提供される仕組みか", category: "必須" },
    { item: "封入率を正確に表示しているか", category: "必須" },
    { item: "特定商取引法に基づく表記があるか", category: "必須" },
    { item: "利用規約・プライバシーポリシーを整備しているか", category: "必須" },
    { item: "年齢確認の仕組みがあるか", category: "推奨" },
    { item: "カスタマーサポート体制があるか", category: "推奨" },
    { item: "抽選の公平性を担保する仕組みがあるか", category: "推奨" },
  ];

  const faqItems = [
    {
      question: "オリパは違法ですか？",
      answer:
        "オリパ自体は違法ではありません。ただし、運営方法によっては賭博罪や景品表示法違反に該当する可能性があります。すべての抽選結果で商品が提供される仕組みであれば、一般的に違法とはなりません。",
    },
    {
      question: "なぜ一部のオリパが問題視されているのですか？",
      answer:
        "「ハズレ」で商品がもらえない仕組みや、虚偽の封入率表示、過度に射幸心を煽る演出などが問題視されています。また、未成年への高額販売なども社会的な批判を受けています。",
    },
    {
      question: "オリパの規制は今後強化されますか？",
      answer:
        "トレーディングカード市場の拡大に伴い、規制強化の議論は続いています。現時点で直接的な規制法はありませんが、既存の法律（賭博罪・景品表示法等）の適用や、業界の自主規制が進む可能性があります。",
    },
    {
      question: "安全なオリパサイトの見分け方は？",
      answer:
        "運営会社の情報が明確、封入率が公開されている、すべての結果で商品が届く、口コミ・評判が良い、といった点を確認しましょう。不明瞭な点が多いサイトは避けることをおすすめします。",
    },
    {
      question: "JoyBaseで運営するオリパは適法ですか？",
      answer:
        "JoyBaseでは、すべての抽選結果で商品が提供される仕組みを採用しています。また、封入率の表示機能、年齢確認機能などを搭載し、適法なオリパ運営をサポートしています。ただし、各店舗の運営方法によりますので、古物商許可証の取得など必要な手続きは各事業者様の責任となります。",
    },
  ];

  return (
    <BlogLayout
      title="オリパの法規制ガイド【2025年最新】"
      description="オリパ（オリジナルパック）は違法？賭博罪との関係、必要な許可証、安全な運営方法・購入方法を事業者・ユーザー両方の視点から詳しく解説します。"
      category="法規制"
      publishedAt="2025年12月1日"
      breadcrumbs={[{ label: "オリパ規制ガイド" }]}
    >
      <Helmet>
        <title>【2025年最新】オリパの法規制ガイド | 違法？賭博罪との関係を解説 | JoyBase</title>
        <meta
          name="description"
          content="オリパ（オリジナルパック）は違法なのか？賭博罪との関係、必要な許可証、安全な運営・購入方法を詳しく解説。事業者・ユーザー両方に役立つ法規制ガイド。"
        />
        <meta
          name="keywords"
          content="オリパ,違法,規制,賭博罪,古物商許可証,オリパ法律,トレカ,オリパ安全"
        />
        <meta property="og:title" content="【2025年最新】オリパの法規制ガイド | JoyBase" />
        <meta
          property="og:description"
          content="オリパは違法？賭博罪との関係、必要な許可証、安全な運営・購入方法を事業者・ユーザー両方の視点から解説。"
        />
        <meta property="og:url" content="https://joybase.jp/blog/oripa-regulation-guide" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://joybase.jp/blog/oripa-regulation-guide" />
      </Helmet>

      {/* 導入 */}
      <section className="mb-12">
        <p>
          オリパ（オリジナルパック）は、トレーディングカードを詰め合わせた商品で、
          何が入っているか分からない「ガチャ」的な楽しみ方ができる人気の販売形態です。
          オリパ市場が拡大する中、<strong>適切な運営ルールを守ること</strong>が
          事業者の信頼性向上やトラブル防止につながります。
        </p>
        <p>
          この記事では、オリパ運営に関連する法規制について、
          <strong>事業者・ユーザー両方の視点</strong>から詳しく解説します。
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-yellow-800 mb-2 text-base">免責事項</h3>
              <p className="text-yellow-700 text-sm m-0">
                本記事は一般的な情報提供を目的としており、法的助言ではありません。
                具体的な判断については、弁護士等の専門家にご相談ください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* オリパと賭博罪の関係 */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <Scale className="w-6 h-6 text-blue-500" />
          オリパと賭博罪の関係
        </h2>

        <p>
          賭博罪（刑法第185条）は、<strong>「偶然の勝敗により財物の得失を争う行為」</strong>
          を処罰対象としています。オリパが賭博罪に該当するかどうかは、
          その<strong>仕組みと運営方法</strong>によって判断されます。
        </p>

        <div className="grid md:grid-cols-2 gap-6 my-6 not-prose">
          <div className="bg-green-50 rounded-lg p-6">
            <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              賭博罪に該当しないケース
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-green-500 mt-0.5">●</span>
                すべての抽選結果で商品（カード）が提供される
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-green-500 mt-0.5">●</span>
                「当たり」「ハズレ」ではなく、内容の違いに過ぎない
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-green-500 mt-0.5">●</span>
                商品の売買であり、金銭の得失を争っていない
              </li>
            </ul>
          </div>

          <div className="bg-red-50 rounded-lg p-6">
            <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              賭博罪に該当する可能性があるケース
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-500 mt-0.5">●</span>
                「ハズレ」で商品がもらえない
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-500 mt-0.5">●</span>
                ポイントバックのみで商品提供なし
              </li>
              <li className="flex items-start gap-2 text-gray-700">
                <span className="text-red-500 mt-0.5">●</span>
                実質的に金銭の得失を争う仕組み
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-blue-800 text-sm m-0">
            <strong>ポイント：</strong>
            重要なのは「すべての結果で商品が提供されるかどうか」です。
            カードの価値に差があっても、必ず商品が届く仕組みであれば、
            一般的に賭博罪には該当しないと考えられています。
          </p>
        </div>
      </section>

      {/* 適法運営の条件 */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-500" />
          適法なオリパ運営の条件
        </h2>

        <div className="space-y-4 not-prose">
          {legalConditions.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-4 bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">{item.condition}</h3>
                <p className="text-gray-600 text-sm m-0">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 問題となりうるケース */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          問題となりうるケース
        </h2>

        <div className="overflow-x-auto not-prose -mx-4 px-4">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  ケース
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  説明
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  リスク
                </th>
              </tr>
            </thead>
            <tbody>
              {problematicCases.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                    {item.case}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-gray-600">
                    {item.description}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        item.level === "high"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {item.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 関連法規 */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-purple-500" />
          オリパに関連する法規
        </h2>

        <div className="space-y-4 not-prose">
          {relatedLaws.map((law, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-bold text-gray-900">{law.name}</h3>
                <span className="text-xs text-gray-500">{law.article}</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{law.summary}</p>
              <p className="text-sm text-purple-700 m-0">
                <strong>オリパとの関連：</strong>
                {law.relevance}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ユーザー向け：安全なオリパの見分け方 */}
      <section className="mb-12">
        <h2>【ユーザー向け】安全なオリパサイトの見分け方</h2>

        <p>
          オリパを購入する際は、以下のポイントをチェックして安全なサイトかどうか確認しましょう。
        </p>

        <div className="grid md:grid-cols-2 gap-4 my-6 not-prose">
          {userTips.map((tip, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                <h3 className="font-bold text-gray-900 text-sm">{tip.title}</h3>
              </div>
              <p className="text-gray-600 text-sm m-0">{tip.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="font-bold text-red-800 mb-2 text-base">こんなサイトは要注意</h3>
          <ul className="text-red-700 text-sm space-y-1 m-0 list-none p-0">
            <li>✗ 運営会社の情報が不明</li>
            <li>✗ 封入率が非公開</li>
            <li>✗ 「ハズレ」で何も届かない</li>
            <li>✗ 口コミや評判が極端に悪い</li>
            <li>✗ 問い合わせ先がない</li>
          </ul>
        </div>
      </section>

      {/* 事業者向け：適法運営チェックリスト */}
      <section className="mb-12">
        <h2>【事業者向け】適法運営チェックリスト</h2>

        <p>
          オリパ販売を行う事業者の方は、以下のチェックリストを参考に適法な運営を心がけましょう。
        </p>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden my-6 not-prose">
          {operatorChecklist.map((item, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 ${
                index !== operatorChecklist.length - 1 ? "border-b border-gray-100" : ""
              }`}
            >
              <div
                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  item.category === "必須" ? "bg-red-100" : "bg-blue-100"
                }`}
              >
                <CheckCircle2
                  className={`w-4 h-4 ${
                    item.category === "必須" ? "text-red-600" : "text-blue-600"
                  }`}
                />
              </div>
              <span className="flex-1 text-sm text-gray-700">{item.item}</span>
              <span
                className={`text-xs font-semibold px-2 py-1 rounded ${
                  item.category === "必須"
                    ? "bg-red-100 text-red-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {item.category}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="font-bold text-green-800 mb-2 text-base">JoyBaseなら安心</h3>
          <p className="text-green-700 text-sm mb-4">
            JoyBaseでは、すべての抽選結果で商品が提供される仕組みを採用。
            封入率の表示機能、年齢確認機能、SMS認証などを搭載し、適法なオリパ運営をサポートします。
          </p>
          <a
            href="https://dashboard.joybase.jp/signup"
            className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            無料で店舗登録
            <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </div>
      </section>

      {/* JoyBaseの取り組み */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-yellow-500" />
          JoyBaseの取り組み
        </h2>

        <p>
          JoyBaseでは、オリパ業界の健全な発展のため、以下の取り組みを行っています。
        </p>

        <div className="space-y-6 my-6 not-prose">
          <div className="bg-white border-l-4 border-yellow-400 rounded-r-lg p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">1. すべての抽選で商品を提供</h3>
            <p className="text-gray-600 text-sm m-0">
              JoyBaseのシステムでは、「ハズレ」で商品がもらえないという仕組みは存在しません。
              すべての抽選結果で必ずカード等の商品が提供される設計になっており、
              賭博罪に該当するリスクを排除しています。
            </p>
          </div>

          <div className="bg-white border-l-4 border-yellow-400 rounded-r-lg p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">2. 封入率の透明性</h3>
            <p className="text-gray-600 text-sm m-0">
              各オリパの封入率を管理画面で設定・表示する機能を提供。
              ユーザーが購入前に確率を確認できる仕組みを標準搭載し、
              景品表示法の遵守をサポートします。
            </p>
          </div>

          <div className="bg-white border-l-4 border-yellow-400 rounded-r-lg p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">3. 本人確認・年齢確認</h3>
            <p className="text-gray-600 text-sm m-0">
              SMS認証機能と年齢確認機能を搭載。
              未成年への過度な販売を防止し、責任あるオリパ運営を支援します。
              なりすまし・複数アカウント対策にも有効です。
            </p>
          </div>

          <div className="bg-white border-l-4 border-yellow-400 rounded-r-lg p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">4. 公正な抽選システム</h3>
            <p className="text-gray-600 text-sm m-0">
              サーバーサイドでの乱数生成により、公正な抽選を実現。
              抽選結果は改ざんできない仕組みになっており、
              ユーザーに安心して利用いただける環境を提供します。
            </p>
          </div>

          <div className="bg-white border-l-4 border-yellow-400 rounded-r-lg p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">5. 法令遵守のサポート</h3>
            <p className="text-gray-600 text-sm m-0">
              特定商取引法に基づく表記テンプレート、利用規約・プライバシーポリシーの
              ひな形を提供。古物商許可証の取得ガイドなど、法令遵守に必要な情報を発信し、
              事業者様の適法な運営をサポートします。
            </p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-800 text-sm m-0">
            <strong>JoyBaseの理念：</strong>
            オリパは本来、カードゲームファンにとって楽しいコンテンツです。
            適切なルールのもとで運営されることで、事業者・ユーザー双方にとって
            価値のある体験を提供できると考えています。
            JoyBaseは、オリパ業界の健全な発展に貢献してまいります。
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2>よくある質問</h2>

        <div className="space-y-4 not-prose">
          {faqItems.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Q: {item.question}</h3>
              <p className="text-gray-600 text-sm m-0">A: {item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* まとめ */}
      <section>
        <h2>まとめ</h2>
        <p>
          オリパは、適切に運営すれば違法ではありません。重要なのは以下の点です：
        </p>
        <ul>
          <li>
            <strong>すべての結果で商品を提供する</strong>（ハズレで商品なしはNG）
          </li>
          <li>
            <strong>封入率を正確に表示する</strong>（虚偽表示は違法）
          </li>
          <li>
            <strong>古物商許可証を取得する</strong>（中古カードを扱う場合は必須）
          </li>
          <li>
            <strong>特定商取引法に基づく表記を整備する</strong>
          </li>
        </ul>
        <p>
          ユーザーの方は、安全なサイトかどうかを見極めて購入しましょう。
          事業者の方は、法令を遵守した適切な運営を心がけることで、
          持続可能なビジネスを築くことができます。
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold text-gray-900 mb-2">関連記事</h3>
          <div className="space-y-2">
            <Link
              to="/blog/kobutsusho-guide"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <ArrowRight className="w-4 h-4" />
              古物商許可証の取り方完全ガイド
            </Link>
            <Link
              to="/blog/oripa-site-comparison"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <ArrowRight className="w-4 h-4" />
              オリパサイト構築方法の比較
            </Link>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
};

export default OripaRegulationGuidePage;
